// The change queue's replay engine (BL-2345, ADR 066 — Offline Mode § 4): sends queued offline
// changes to the server in order, one request per entry (a lot's back-to-back edits merge into one
// save), and owns every status move (`pending | syncing | failed | refused`, signed 2026-10-09).
// It is the sync engine's ChangeQueueUploader (BL-2344), so the queue uploads before each download.
import {
  CHANGE_QUEUE_CREATE_OPS,
  lotGroupKey,
  readQueuedEntry,
  type ChangeQueueCall,
  type ChangeQueueOp,
  type ChangeQueuePayload,
  type QueuedEntryInput,
} from '@auctogon/admin-isomorphic/isomorphic/offline/change-queue-entry';
import { MAX_REPLAY_ATTEMPTS, classifyReplayResponse, type ReplayOutcome } from '@auctogon/admin-isomorphic/isomorphic/offline/change-queue-outcome';
import { type LotDesiredState } from '@auctogon/admin-isomorphic/isomorphic/offline/lot-desired-state';
import { nextRunnable, substituteId } from '@auctogon/admin-isomorphic/isomorphic/offline/change-queue-plan';
import type {
  OfflineMediaStaging,
  OfflineStore,
  OutboxAppendResult,
  OutboxEntry,
  OutboxLocalWriteInput,
  OutboxLocalWriteResult,
  OutboxUpdateInput,
} from '../../gateways/native-device';
import type { ChangeQueueUploader, ChangeQueueUploadResult } from '../offline-sync/change-queue-uploader';
import { alreadyStored, asBody, failure, stagedFilesOf, uploadFiles } from './change-queue-uploads';
import { closedQueuesSettled, holdScopeWhileSettling } from './change-queue-settling';
import { discardEntry, retryEntry } from './change-queue-entry-actions';
import { sendLotStatesRound } from './lot-state-executor';
import {
  discardLotState,
  overwriteLotState,
  readLotStates,
  rekeyLotState,
  retryLotState,
  senderStore,
} from './lot-state-actions';
import { MalformedChangeError, ReplayTransportError, type ReplayTransport } from './replay-transport';

/** Outcome of Retry, Discard and Overwrite. */
export type ChangeQueueActionResult =
  | { success: true }
  | { success: false; code: 'NOT_FOUND' | 'NOT_FAILED' | 'NOT_REFUSED' };

/** The change queue: the sync engine's uploader, plus the queue's own actions. */
export interface ChangeQueueReplay extends ChangeQueueUploader {
  /**
   * Queue one change (BL-2431's offline writes call this).
   *
   * @param input - an entry from `buildQueuedEntry`.
   * @returns the stored entry, or `DUPLICATE_ID`.
   * @sideEffect Appends to the device's queue, which also resets the store's idle clock.
   */
  enqueue(input: QueuedEntryInput): Promise<OutboxAppendResult>;
  /**
   * Queue one offline write (BL-2431): its entries, built by `buildQueuedEntry`, and the device's lot
   * records it changes, committed together so the offline screens show the change.
   *
   * @param input - the entries and the lot records to put and delete.
   * @returns the stored entries, or `DUPLICATE_ID` with nothing written.
   * @sideEffect Appends to the device's queue and rewrites lot records; notifies subscribers.
   */
  queueLocalWrite(input: OutboxLocalWriteInput): Promise<OutboxLocalWriteResult>;
  /** @returns every queued change, in queue order (the Change queue tab's rows, BL-2438). */
  list(): Promise<OutboxEntry[]>;
  /** @returns every lot's desired state (BL-2681), the lots the queue has something to send for. */
  listLotStates(): Promise<LotDesiredState[]>;
  /**
   * Send a failed change again on the next pass.
   *
   * @param id - the entry's id, or a lot's id for its desired state.
   * @returns the outcome.
   * @sideEffect Marks the entry (and a merged edit's siblings) pending.
   */
  retry(id: string): Promise<ChangeQueueActionResult>;
  /**
   * Drop a failed or refused change, and what waits on it. Nothing is discarded before it fails.
   *
   * @param id - the entry's id, or a lot's id for its desired state (Keep).
   * @returns the outcome.
   * @sideEffect Removes entries and their staged files; marks the lot's auction for a full download.
   */
  discard(id: string): Promise<ChangeQueueActionResult>;
  /**
   * Resend a refused edit with `overwrite`, writing only the fields it edited (ADR 066 § 4.1), or a
   * refused photo order with `overwrite`, so it replaces the newer order (BL-2418).
   *
   * @param id - the entry's id, or a lot's id for its desired state.
   * @returns the outcome.
   * @sideEffect Marks the lot's state, or the order, pending with `overwrite: true`.
   */
  overwrite(id: string): Promise<ChangeQueueActionResult>;
  /**
   * @param listener - called after every change to the queue.
   * @returns the unsubscribe function.
   */
  subscribe(listener: () => void): () => void;
  /**
   * Stop this queue: a pass in flight sends nothing more after its current request, and a later
   * pass changes nothing (BUG-2064). Called when the signed-in tenant or user changes, so one
   * scope's changes never replay under another session.
   */
  close(): void;
}

/** What the replay needs. */
export interface ChangeQueueReplayDeps {
  store: OfflineStore;
  /** The scope's staged files, or null in a build without them (a file's add then fails). */
  media: OfflineMediaStaging | null;
  transport: ReplayTransport;
  /** False while the app is offline, automatic or manual: the pass stops before the next entry. */
  isOnline: () => boolean;
  /** False once another session owns the device (BUG-2064): the pass stops. Omitted means true. */
  isActiveScope?: () => Promise<boolean>;
  logError?: (message: string, err: Error) => void;
}

/**
 * Build the replay for one scope's queue.
 *
 * @param deps - the store, staged files, transport and online check.
 * @returns the replay.
 */
export function createChangeQueueReplay(deps: ChangeQueueReplayDeps): ChangeQueueReplay {
  const listeners = new Set<() => void>();
  const notify = () => listeners.forEach((listener) => listener());
  let running: Promise<ChangeQueueUploadResult> | null = null;
  let closed = false;
  const ctx: PassContext = { deps, notify, isClosed: () => closed, view: null };
  // BUG-2064: a closed queue of this scope still sending writes its outcome with no one listening
  // (close() drops its listeners), so this queue announces it: its readers then re-read the outbox.
  const closing = new AbortController();
  void closedQueuesSettled(deps.store.scope, closing.signal)?.then(notify);

  return {
    upload: ({ signal }) => {
      if (running != null) return running;
      // Only a re-opened scope waits (BUG-2064); otherwise the first read starts at once (BUG-2060).
      const settling = closedQueuesSettled(deps.store.scope, signal);
      const pass = settling == null ? runPass(ctx, signal) : settling.then(() => runPass(ctx, signal));
      running = pass.finally(() => {
        running = null;
      });
      return running;
    },
    enqueue: async (input) => {
      const result = await outsideWrite(ctx, () => deps.store.outbox.append(input));
      notify();
      return result;
    },
    queueLocalWrite: async (input) => {
      const result = await outsideWrite(ctx, () => deps.store.outbox.appendLocalWrite(input));
      if (result.success) notify();
      return result;
    },
    // While a pass runs, the tab's re-read on each notify is served from the pass's copy (BUG-2060).
    list: async () => [...(await readQueue(ctx, { keep: false }))],
    // Served from the pass's copy while one runs, like `list` (BUG-2060): the tab re-reads on every notify.
    listLotStates: async () => [...(await readLotStates(ctx, { keep: false }))],
    retry: (id) => act(ctx, async () => (await retryLotState(deps.store, id)) ?? retryEntry(deps.store, id)),
    discard: (id) => act(ctx, async () => (await discardLotState(deps, id)) ?? discardEntry(deps, id)),
    // Only a lot's desired state offers Overwrite: no queued entry's refusal can be resent over a newer value.
    overwrite: (id) => act(ctx, async () => (await overwriteLotState(deps.store, id)) ?? { success: false, code: 'NOT_REFUSED' }),
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    close: () => {
      closed = true;
      closing.abort();
      listeners.clear();
      if (running != null) holdScopeWhileSettling(deps.store.scope, running);
    },
  };
}

interface PassContext {
  deps: ChangeQueueReplayDeps;
  notify: () => void;
  isClosed: () => boolean;
  /** The running pass's copy of the queue, or null between passes (BUG-2060). */
  view: QueueView | null;
}

/**
 * BUG-2060: the queue as a pass last read or wrote it, so a big queue replays in O(n) outbox reads
 * instead of a whole listing per entry. Only the pass lists into it, and the pass never writes while
 * its own listing is in flight, so its own writes' results keep it current. A change made through
 * this queue's own actions (a queued write, Retry, Discard, Overwrite) marks it stale as it starts
 * and as it ends, and the pass's next read lists again.
 */
interface QueueView {
  entries: OutboxEntry[] | null;
  /** The lots' desired states, kept in step the same way (BL-2681). */
  lotStates: LotDesiredState[] | null;
  /** Bumped by every stale mark, so a pass listing that raced an outside write is not kept. */
  version: number;
}

function markStale(ctx: PassContext): void {
  if (ctx.view == null) return;
  ctx.view.entries = null;
  ctx.view.lotStates = null;
  ctx.view.version += 1;
}

/**
 * The queue, in `seq` order: the pass's copy while it is current, else one listing. Only the pass's
 * own read keeps its listing (`keep`), and only when no outside write started while it was in
 * flight; a listing the tab takes is never kept, so it can't replace a copy the pass has since patched.
 */
async function readQueue(ctx: PassContext, opts: { keep: boolean }): Promise<OutboxEntry[]> {
  const view = ctx.view;
  if (view?.entries != null) return view.entries;
  const version = view?.version;
  const entries = await ctx.deps.store.outbox.list();
  if (opts.keep && view != null && ctx.view === view && view.version === version) view.entries = entries;
  return entries;
}




/** A write through this queue's own actions: the pass's copy is stale while it runs and after it. */
async function outsideWrite<T>(ctx: PassContext, write: () => Promise<T>): Promise<T> {
  markStale(ctx);
  try {
    return await write();
  } finally {
    markStale(ctx);
  }
}

/** Write one entry's replay state, and keep the pass's copy in step with what the store wrote. */
async function writeEntry(ctx: PassContext, input: OutboxUpdateInput): Promise<void> {
  const result = await ctx.deps.store.outbox.update(input);
  const entries = ctx.view?.entries;
  if (entries == null) return;
  const at = entries.findIndex((e) => e.id === input.id);
  if (at === -1) return;
  if (result.success) entries[at] = result.entry;
  else entries.splice(at, 1);
}

/** Remove one entry, from the store and the pass's copy. */
async function removeEntry(ctx: PassContext, id: string): Promise<void> {
  await ctx.deps.store.outbox.remove(id);
  const entries = ctx.view?.entries;
  const at = entries?.findIndex((e) => e.id === id) ?? -1;
  if (entries != null && at !== -1) entries.splice(at, 1);
}

async function act(ctx: PassContext, task: () => Promise<ChangeQueueActionResult>): Promise<ChangeQueueActionResult> {
  const result = await outsideWrite(ctx, task);
  if (result.success) ctx.notify();
  return result;
}

/** Not aborted, closed or offline, and still the session's scope. Sync with no session check. */
function maySend(ctx: PassContext, signal: AbortSignal): boolean | Promise<boolean> {
  if (signal.aborted || ctx.isClosed() || !ctx.deps.isOnline()) return false;
  return ctx.deps.isActiveScope?.() ?? true;
}

async function runPass(ctx: PassContext, signal: AbortSignal): Promise<ChangeQueueUploadResult> {
  ctx.view = { entries: null, lotStates: null, version: 0 };
  try {
    return await sendQueue(ctx, signal);
  } catch (err) {
    // BUG-2466: a pass that stops on a throw (a 401, a dropped connection) wrote its entries back to
    // pending after the tab's last re-read. Announcing the stop, once the pass's copy is gone, makes the
    // tab read the store instead of staying on "Syncing". A clean pass already announced each write.
    ctx.view = null;
    ctx.notify();
    throw err;
  } finally {
    ctx.view = null;
  }
}

async function sendQueue(ctx: PassContext, signal: AbortSignal): Promise<ChangeQueueUploadResult> {
  const result: ChangeQueueUploadResult = { attempted: 0, failed: 0 };
  // BUG-2064: a closed queue changes nothing (a newer queue may have this entry in flight).
  // Not awaited when sync, so the pass's first read starts at once (BUG-2060's ordering).
  const may = maySend(ctx, signal);
  if (!(typeof may === 'boolean' ? may : await may)) return result;
  // A kill mid-request leaves its entry `syncing`; it is sent again (creates and per-item media
  // are idempotent, BL-2337 and BL-2338; a repeat status change or move reads as synced).
  for (const e of [...(await readQueue(ctx, { keep: true }))]) {
    if (e.status === 'syncing') await writeEntry(ctx, { id: e.id, status: 'pending' });
  }
  const tried = new Set<string>();
  const triedStates = new Set<string>();
  const sender = {
    store: senderStore(ctx, () => readQueue(ctx, { keep: true })),
    media: ctx.deps.media,
    transport: ctx.deps.transport,
    notify: ctx.notify,
    maySend: () => maySend(ctx, signal),
    logError: ctx.deps.logError,
  };
  // BL-2681: a lot's desired state goes first, so its edit lands ahead of a move or status change;
  // what that releases (a relist's copy, a lot with a follow-up edit) goes in the next round.
  for (let round = 0; round < MAX_ROUNDS; round += 1) {
    const states = await sendLotStatesRound(sender, { signal, tried: triedStates });
    result.attempted += states.attempted;
    result.failed += states.failed;
    const lane = await sendLane(ctx, { signal, tried, result });
    if (states.attempted === 0 && lane === 0) break;
  }
  return result;
}

/** A pass sends states then the ordered lane again while either made progress, up to this many rounds. */
const MAX_ROUNDS = 4;

/** Send the ordered lane once; returns how many groups landed. */
async function sendLane(
  ctx: PassContext,
  input: { signal: AbortSignal; tried: Set<string>; result: ChangeQueueUploadResult },
): Promise<number> {
  const { signal, tried, result } = input;
  // A lot's entries wait while its desired state is unsettled (BL-2681).
  const blocked = new Set((await readLotStates(ctx, { keep: true })).map((state) => lotGroupKey(state.lotId)));
  let landed = 0;
  for (;;) {
    if (!(await maySend(ctx, signal))) break;
    // Read synchronously below, before the first await: the pass's writes then patch this copy in place.
    const entries = await readQueue(ctx, { keep: true });
    const head = nextRunnable(entries, tried, blocked);
    if (!head) break;
    // An entry the queue cannot read runs alone and fails with its reason.
    result.attempted += 1;
    const ended = await replayEntry(ctx, { entry: head, signal });
    if (ended === 'failed') result.failed += 1;
    if (ended === 'retry') tried.add(head.id);
    if (ended === 'synced') landed += 1;
  }
  return landed;
}

async function replayEntry(ctx: PassContext, input: { entry: OutboxEntry; signal: AbortSignal }): Promise<'synced' | 'failed' | 'retry'> {
  const { entry } = input;
  await writeEntry(ctx, { id: entry.id, status: 'syncing' });
  ctx.notify();
  let outcome: ReplayOutcome;
  try {
    outcome = await sendEntry(ctx.deps, { entry, signal: input.signal });
  } catch (err) {
    await writeEntry(ctx, { id: entry.id, status: 'pending' });
    ctx.notify();
    throw new ReplayTransportError('change queue: the connection failed', { cause: err });
  }
  const ended = await recordOutcome(ctx, { entry, outcome });
  ctx.notify();
  return ended;
}

/** Send one entry. Throws only when the server could not be reached. */
async function sendEntry(deps: ChangeQueueReplayDeps, input: { entry: OutboxEntry; signal: AbortSignal }): Promise<ReplayOutcome> {
  const { entry, signal } = input;
  const read = readQueuedEntry(entry);
  if (!read.ok) return failure(read.reason);
  const { op, payload } = read;
  let call: ChangeQueueCall = payload.call;
  // A file already in storage (uploaded before going offline, BL-2447 Q5) is added as it is.
  if (op === 'lot.document.add' && !alreadyStored(payload)) {
    const uploaded = await uploadFiles(deps, payload);
    if (!uploaded.ok) return uploaded.outcome;
    call = { ...call, body: { ...asBody(call.body), ...uploaded.paths } };
  }
  try {
    const res = await deps.transport.send({ op, call, signal });
    return classifyReplayResponse({ op, status: res.status, body: res.body });
  } catch (err) {
    if (err instanceof MalformedChangeError) return failure(err.message);
    throw err;
  }
}

async function recordOutcome(
  ctx: PassContext,
  input: { entry: OutboxEntry; outcome: ReplayOutcome },
): Promise<'synced' | 'failed' | 'retry'> {
  const { entry, outcome } = input;
  const attempts = entry.attempts + 1;
  switch (outcome.kind) {
    case 'synced':
      await finishSynced(ctx, { entry, serverId: outcome.serverId });
      return 'synced';
    case 'refused':
      await writeEntry(ctx, { id: entry.id, status: 'refused', error: outcome.error, attempts });
      return 'failed';
    case 'failed':
      await writeEntry(ctx, { id: entry.id, status: 'failed', error: outcome.error, attempts });
      return 'failed';
    case 'retry-later': {
      const status = attempts >= MAX_REPLAY_ATTEMPTS ? 'failed' : 'pending';
      await writeEntry(ctx, { id: entry.id, status, error: outcome.error, attempts });
      return status === 'failed' ? 'failed' : 'retry';
    }
    case 'transport':
      await writeEntry(ctx, { id: entry.id, status: 'pending' });
      throw new ReplayTransportError(`change queue: ${outcome.reason}`);
  }
}

/**
 * A synced entry leaves the queue; a create first points everything waiting on it at its server id.
 * The entry goes before its staged files (BUG-2070): a file that can't be removed, or a kill between
 * the two, leaves an ownerless file the next launch's sweep removes, never a landed add whose file
 * is gone, which the next pass would fail.
 */
async function finishSynced(
  ctx: PassContext,
  input: { entry: OutboxEntry; serverId: string | null },
): Promise<void> {
  const { store } = ctx.deps;
  const head = input.entry;
  if (CHANGE_QUEUE_CREATE_OPS.has(head.op as ChangeQueueOp) && input.serverId != null) {
    // Entries queued, or rewritten, while the create was in flight must follow it too: a write
    // through this queue marks the pass's copy stale, so this read lists again (BUG-2060).
    const entries = [...(await readQueue(ctx, { keep: true }))];
    await repointWaiting(ctx, { entries, clientId: head.id, serverId: input.serverId, lot: head.op !== 'note.create' });
    // BL-2681: edits made to a relist's copy while it was queued follow it to its server id.
    if (head.op === 'lot.relist') {
      await rekeyLotState(store, { clientId: head.id, serverId: input.serverId });
      // The re-key wrote the states outside the sender's store, so the pass lists them again.
      if (ctx.view != null) ctx.view.lotStates = null;
    }
    // The device's temporary "New lot" goes; the next sync brings the server's copy.
    if (head.op !== 'note.create') await store.records.delete({ collection: 'lots', key: head.id });
  }
  await removeEntry(ctx, head.id);
  const read = readQueuedEntry(head);
  if (read.ok) for (const id of stagedFilesOf(read.payload)) await dropSyncedFile(ctx.deps, id);
}

async function dropSyncedFile(deps: ChangeQueueReplayDeps, id: string): Promise<void> {
  try {
    await deps.media?.delete(id);
  } catch (err) {
    deps.logError?.(
      "change queue: a synced change's staged file could not be removed; the next launch's sweep removes it",
      new Error(`staged file ${id} not removed`, { cause: err }),
    );
  }
}

async function repointWaiting(
  ctx: PassContext,
  input: { entries: OutboxEntry[]; clientId: string; serverId: string; lot: boolean },
): Promise<void> {
  const { clientId, serverId } = input;
  const group = input.lot ? lotGroupKey(clientId) : null;
  for (const e of input.entries) {
    if (e.id === clientId || (e.dependsOn !== clientId && (group == null || e.groupKey !== group))) continue;
    const read = readQueuedEntry(e);
    if (!read.ok) continue;
    const payload: ChangeQueuePayload = { ...read.payload, call: substituteId(read.payload.call, clientId, serverId) };
    await writeEntry(ctx, { id: e.id, payload, dependsOn: e.dependsOn === clientId ? null : e.dependsOn });
  }
}









